/** QCheck / creative validation reason codes (Amplitude `rejection_reasons` values). */
export const CREATIVE_REJECTION_REASON_CODES = {
  AV_SYNC: 'av_sync',
  AUDIO_EXTRA_CHANNELS: 'audio_extra_channels',
  AUDIO_MISSING: 'audio_missing',
  FRAMES_BLENDED: 'frames_blended',
  COMPETITOR_CALLOUT: 'competitor_callout',
  FILE_CORRUPTED: 'file_corrupted',
  FRAMES_DUPLICATE: 'frames_duplicate',
  GLITCHES_OR_ARTIFACTS: 'glitches_or_artifacts',
  SLATE_PRESENT: 'slate_present',
  IMPLIED_INTERACTIVITY: 'implied_interactivity',
  WRONG_ASPECT_RATIO: 'wrong_aspect_ratio',
  VIDEO_WRONG_CODEC: 'video_wrong_codec',
  AUDIO_WRONG_CODEC: 'audio_wrong_codec',
  FRAMES_WRONG_RATE: 'frames_wrong_rate',
  FRAMES_INTERLACED: 'frames_interlaced',
  INTERNAL_ERROR: 'internal_error',
  INVALID_URL: 'invalid_url',
  AUDIO_WRONG_BIT_RATE: 'audio_wrong_bit_rate',
  VIDEO_WRONG_BIT_RATE: 'video_wrong_bit_rate',
  VIDEO_EXTRA_TRACKS: 'video_extra_tracks',
  AV_DURATION_MISMATCH: 'av_duration_mismatch',
  AD_POLICIES_VIOLATION: 'ad_policies_violation',
  BRAND_MISMATCH: 'brand_mismatch',
  FILE_WRONG_FORMAT: 'file_wrong_format',
  FILE_WRONG_SIZE: 'file_wrong_size',
  WRONG_LENGTH: 'wrong_length',
  WRONG_SAMPLING_RATE: 'wrong_sampling_rate',
  WRONG_SCAN_TYPE: 'wrong_scan_type',
  POLICY_RELIGION: 'policy_religion',
  OTHER: 'other',
  SELF_SERVICE_AD_POLICY_VIOLATION: 'self_service_ad_policy_violation',
} as const;

export const CREATIVE_REJECTION_REASON_UNKNOWN = 'unknown';

/**
 * API `rejectionReason` / `rejectionReasons` entries are user-facing messages.
 * Map exact message text to stable reason codes for analytics.
 */
export const CREATIVE_REJECTION_REASON_MESSAGE_TO_CODE: Record<string, string> = {
  'The audio and video are out of sync.': CREATIVE_REJECTION_REASON_CODES.AV_SYNC,
  'Please reduce the number of audio channels to two channels and map to stereo.':
    CREATIVE_REJECTION_REASON_CODES.AUDIO_EXTRA_CHANNELS,
  'We did not detect any audio. All Ads must have audio.': CREATIVE_REJECTION_REASON_CODES.AUDIO_MISSING,
  'Please deinterlace the Ad without using blended frames.': CREATIVE_REJECTION_REASON_CODES.FRAMES_BLENDED,
  'Please remove the logo and/or call to action.': CREATIVE_REJECTION_REASON_CODES.COMPETITOR_CALLOUT,
  'We were unable to read and transcode your Ad. Please reencode and resubmit it.':
    CREATIVE_REJECTION_REASON_CODES.FILE_CORRUPTED,
  'Please resubmit in native frame rate to remove stutter during playback.':
    CREATIVE_REJECTION_REASON_CODES.FRAMES_DUPLICATE,
  'There are glitches and/or artifacts. Please reencode and resubmit.':
    CREATIVE_REJECTION_REASON_CODES.GLITCHES_OR_ARTIFACTS,
  'Please remove the slate.': CREATIVE_REJECTION_REASON_CODES.SLATE_PRESENT,
  'Please remove buttons, overlays, animations or other elements that imply interactivity.':
    CREATIVE_REJECTION_REASON_CODES.IMPLIED_INTERACTIVITY,
  'Please resubmit your Ad in a widescreen (16:9) format with 1920X1080 or 1280X720 resolution.':
    CREATIVE_REJECTION_REASON_CODES.WRONG_ASPECT_RATIO,
  'Please resubmit your Ad in Apple ProRes 422 or H.264 codecs.': CREATIVE_REJECTION_REASON_CODES.VIDEO_WRONG_CODEC,
  'Please resubmit your Ad in AAC or PCM audio codecs.': CREATIVE_REJECTION_REASON_CODES.AUDIO_WRONG_CODEC,
  'Your video uses a variable or unsupported frame rate. Please re-encode it using a constant frame rate of 23.98, 24, 25, 29.97, or 30 fps.':
    CREATIVE_REJECTION_REASON_CODES.FRAMES_WRONG_RATE,
  'The video is interlaced. Please resubmit with progressive scan.': CREATIVE_REJECTION_REASON_CODES.FRAMES_INTERLACED,
  'There was an internal error which is currently under investigation.': CREATIVE_REJECTION_REASON_CODES.INTERNAL_ERROR,
  'Invalid URL. The Ad contains an invalid URL. Please correct and resubmit.':
    CREATIVE_REJECTION_REASON_CODES.INVALID_URL,
  'Ad audio must use a constant bitrate between 192-256 Kbps and a 48 kHz sample rate.':
    CREATIVE_REJECTION_REASON_CODES.AUDIO_WRONG_BIT_RATE,
  'Ad video bitrate must be a minimum of 10 Mbps.': CREATIVE_REJECTION_REASON_CODES.VIDEO_WRONG_BIT_RATE,
  'There was no detected video track in the Ad.': CREATIVE_REJECTION_REASON_CODES.VIDEO_EXTRA_TRACKS,
  'Both tracks must match the video in duration.': CREATIVE_REJECTION_REASON_CODES.AV_DURATION_MISMATCH,
  'Brand Mismatch.': CREATIVE_REJECTION_REASON_CODES.BRAND_MISMATCH,
  'Your file must be in MOV or MPEG-4 (MP4) format. Save or export it as MOV or MP4, then upload it again.':
    CREATIVE_REJECTION_REASON_CODES.FILE_WRONG_FORMAT,
  'File must be less than 10 GB.': CREATIVE_REJECTION_REASON_CODES.FILE_WRONG_SIZE,
  'Wrong Length. Must be at least 5 seconds and less than 91 seconds.': CREATIVE_REJECTION_REASON_CODES.WRONG_LENGTH,
  'Audio Sampling Rate must be 48 kHz.': CREATIVE_REJECTION_REASON_CODES.WRONG_SAMPLING_RATE,
  'Must be Progressive Scan Type.': CREATIVE_REJECTION_REASON_CODES.WRONG_SCAN_TYPE,
  'Standards and Practices.  The Ad does not adhere to our Technical Specifications and Advertising Policies. Please review the guidelines and re-submit with a different Ad.':
    CREATIVE_REJECTION_REASON_CODES.POLICY_RELIGION,
  // Shared copy for ad_policies_violation, other, and self_service_ad_policy_violation from QCheck.
  "Your Ad does not comply with Disney's Advertising Specifications, Policies, and Guidelines. Please review the Specification, Policies, and Guidelines and resubmit with a different Ad.":
    CREATIVE_REJECTION_REASON_CODES.AD_POLICIES_VIOLATION,
};

export const CREATIVE_REJECTION_REASON_CODE_VALUES = new Set<string>(Object.values(CREATIVE_REJECTION_REASON_CODES));
